import React, { useState } from 'react';
import { Table, Button, Space, Tag, Modal, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, InfoCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined, GiftOutlined } from '@ant-design/icons';
import moment from 'moment';

import AdminAnnouncementForm from '../../../components/admin/AdminAnnouncementForm';
import { useAdminAnnouncements } from '../../../models/useAdminAnnouncements';

const AdminAnnouncementsScreen = () => {
    const {
        allAnnouncements,
        loading,
        showArchived,
        setShowArchived,
        getFilteredAnnouncements,
        archiveAnnouncement,
        toggleStatus,
        saveAnnouncement
    } = useAdminAnnouncements();

    const [showForm, setShowForm] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    const handleAddAnnouncement = () => {
        console.log('Add button clicked!'); // Debug log
        setShowForm(true);
        setEditingAnnouncement(null);
    };

    const handleEditAnnouncement = (announcement) => {
        setShowForm(true);
        setEditingAnnouncement(announcement);
    };

    const handleDeleteAnnouncement = async (id) => {
        await archiveAnnouncement(id);
    };

    const handleToggleArchived = () => {
        setShowArchived(!showArchived);
    };

    const handleToggleStatus = async (id, currentStatus) => {
        await toggleStatus(id, currentStatus);
    };

    const handleFormSubmit = async (formData) => {
        setFormLoading(true);
        const success = await saveAnnouncement(formData, editingAnnouncement);
        
        if (success) {
            setShowForm(false);
            setEditingAnnouncement(null);
        }
        setFormLoading(false);
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setEditingAnnouncement(null);
    };

    const isExpired = (announcement) => {
        if (!announcement.endAt) return false;
        return moment().isAfter(moment(announcement.endAt));
    };

    const getEffectiveStatus = (announcement) => {
        if (announcement.status === 'archived') return 'archived';
        if (isExpired(announcement)) return 'expired';
        return announcement.status;
    };

    const getStatusConfig = (effectiveStatus) => {
        const statusConfig = {
            active: { color: 'green', text: 'Active' },
            paused: { color: 'orange', text: 'Paused' },
            completed: { color: 'gray', text: 'Completed' },
            archived: { color: 'default', text: 'Archived' },
            expired: { color: 'red', text: 'Expired' }
        };
        return statusConfig[effectiveStatus] || { color: 'default', text: effectiveStatus };
    };

    const renderClickableStatus = (config, effectiveStatus, id, status) => {
        return (
            <Button
                type="primary"
                size="small"
                style={{ 
                    width: '100%',
                    fontSize: window.innerWidth < 768 ? '11px' : '12px',
                    backgroundColor: config.color === 'green' ? '#52c41a' : '#fa8c16',
                    borderColor: config.color === 'green' ? '#52c41a' : '#fa8c16',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    userSelect: 'none'
                }}
                onClick={() => handleToggleStatus(id, status)}
                title={`Click to ${effectiveStatus === 'active' ? 'pause' : 'activate'} announcement`}
            >
                {config.text}
            </Button>
        );
    };

    const renderCenteredStatus = (config) => {
        return (
            <div style={{ 
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    backgroundColor: config.color === 'red' ? '#ff4d4f' : '#d9d9d9',
                    color: config.color === 'red' ? '#fff' : '#000',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: window.innerWidth < 768 ? '11px' : '12px',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    minHeight: '24px'
                }}>
                    {config.text}
                </div>
            </div>
        );
    };

    const getStatusTag = (status, id, clickable = false, announcement = null) => {
        const effectiveStatus = announcement ? getEffectiveStatus(announcement) : status;
        const config = getStatusConfig(effectiveStatus);
        
        if (clickable && (effectiveStatus === 'active' || effectiveStatus === 'paused')) {
            return renderClickableStatus(config, effectiveStatus, id, status);
        }
        
        if (effectiveStatus === 'archived' || effectiveStatus === 'expired') {
            return renderCenteredStatus(config);
        }
        
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getTypeIcon = (type) => {
        const iconConfig = {
            info: { icon: <InfoCircleOutlined />, color: '#1890ff' },
            warning: { icon: <ExclamationCircleOutlined />, color: '#fa8c16' },
            error: { icon: <CloseCircleOutlined />, color: '#f5222d' },
            marketing: { icon: <GiftOutlined />, color: '#722ed1' }
        };
        const config = iconConfig[type] || { icon: <InfoCircleOutlined />, color: '#666' };
        return (
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                backgroundColor: config.color,
                color: 'white',
                fontSize: '9px',
                marginRight: '8px',
                border: '1px solid rgba(0,0,0,0.1)'
            }}>
                {config.icon}
            </div>
        );
    };


    // Extract date format strings to reduce nested ternaries
    const getDateFormat = () => {
        return window.innerWidth < 768 ? 'MM/DD HH:mm' : 'MMM DD, YYYY HH:mm';
    };

    const announcements = getFilteredAnnouncements();

        const columns = [
            {
                title: 'Actions',
                key: 'actions',
                width: window.innerWidth < 768 ? 120 : 140,
                fixed: 'left',
                render: (_, record) => (
                    <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'stretch'
                    }}>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => handleEditAnnouncement(record)}
                            size="small"
                            style={{ 
                                width: '100%',
                                fontSize: window.innerWidth < 768 ? '11px' : '12px'
                            }}
                        >
                            Edit
                        </Button>
                        {getStatusTag(record.status, record.id, true, record)}
                    </div>
                ),
            },
            {
                title: 'Announcement',
                key: 'announcement',
                width: window.innerWidth < 768 ? 200 : 350,
                render: (_, record) => (
                    <div style={{ 
                        marginBottom: window.innerWidth < 768 ? '4px' : '0px'
                    }}>
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px'
                        }}>
                            {getTypeIcon(record.type)}
                            <div style={{ 
                                fontSize: window.innerWidth < 768 ? '12px' : '14px',
                                fontWeight: 'bold',
                                color: '#1890ff',
                                marginRight: '8px'
                            }}>
                                {record.headline}
                            </div>
                            <div style={{
                                backgroundColor: '#f0f0f0',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                color: '#666'
                            }}>
                                {record.count || 0}
                            </div>
                        </div>
                        <div style={{ 
                            fontSize: window.innerWidth < 768 ? '11px' : '13px',
                            lineHeight: '1.3',
                            color: '#666',
                            marginBottom: '4px'
                        }}>
                            {record.message}
                        </div>
                        <div style={{ 
                            fontSize: window.innerWidth < 768 ? '9px' : '10px',
                            color: '#999',
                            fontStyle: 'italic'
                        }}>
                            Created: {record.createdAt ? moment(record.createdAt).format(getDateFormat()) : 'N/A'}
                        </div>
                    </div>
                ),
            },
            {
                title: 'Dates',
                key: 'dates',
                width: window.innerWidth < 768 ? 120 : 180,
                render: (_, record) => (
                    <div style={{ 
                        fontSize: window.innerWidth < 768 ? '10px' : '12px',
                        lineHeight: '1.2',
                        marginBottom: window.innerWidth < 768 ? '4px' : '0px'
                    }}>
                        <div style={{ marginBottom: '2px' }}>
                            <strong>Start:</strong> {record.startAt ? moment(record.startAt).format(getDateFormat()) : 'N/A'}
                        </div>
                        <div style={{ 
                            textAlign: 'center', 
                            color: '#ccc', 
                            margin: '2px 0',
                            fontSize: '8px'
                        }}>
                            ──────────
                        </div>
                        <div>
                            <strong>End:</strong> {record.endAt ? moment(record.endAt).format(getDateFormat()) : 'N/A'}
                        </div>
                    </div>
                ),
            },
            {
                title: 'Archive',
                key: 'archive',
                width: window.innerWidth < 768 ? 80 : 100,
                fixed: 'right',
                render: (_, record) => (
                    <Popconfirm
                        title="Are you sure you want to archive this announcement?"
                        onConfirm={() => handleDeleteAnnouncement(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            style={{ 
                                width: window.innerWidth < 768 ? '100%' : 'auto',
                                minWidth: window.innerWidth < 768 ? 'auto' : '60px',
                                fontSize: window.innerWidth < 768 ? '11px' : '12px'
                            }}
                        >
                            {window.innerWidth < 768 ? 'Archive' : ''}
                        </Button>
                    </Popconfirm>
                ),
            },
        ];

        return (
            <div style={{ padding: window.innerWidth < 768 ? '12px' : '24px' }}>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
                    marginBottom: '16px',
                    gap: window.innerWidth < 768 ? '12px' : '0'
                }}>
                    <h2 style={{ 
                        margin: 0,
                        fontSize: window.innerWidth < 768 ? '18px' : '24px'
                    }}>
                        Announcements
                    </h2>
                    <div style={{ 
                        display: 'flex',
                        gap: '8px',
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row'
                    }}>
                        <Button
                            type={showArchived ? 'primary' : 'default'}
                            onClick={handleToggleArchived}
                            style={{
                                width: window.innerWidth < 768 ? '100%' : 'auto',
                                minWidth: window.innerWidth < 768 ? 'auto' : '140px'
                            }}
                        >
                            {showArchived ? 'Hide Archived' : 'Show Archived'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddAnnouncement}
                            style={{
                                width: window.innerWidth < 768 ? '100%' : 'auto',
                                minWidth: window.innerWidth < 768 ? 'auto' : '140px'
                            }}
                        >
                            Add Announcement
                        </Button>
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={announcements}
                    loading={loading}
                    rowKey="id"
                    scroll={{ 
                        y: window.innerHeight < 600 ? 400 : undefined
                    }}
                    size={window.innerWidth < 768 ? 'small' : 'middle'}
                    tableLayout="fixed"
                    pagination={{
                        pageSize: window.innerWidth < 768 ? 10 : 20,
                        showSizeChanger: true,
                        showQuickJumper: window.innerWidth >= 768,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} announcements`,
                        simple: window.innerWidth < 768,
                        size: window.innerWidth < 768 ? 'small' : 'default'
                    }}
                />

                <Modal
                    title={editingAnnouncement ? 'Edit Announcement' : 'Add New Announcement'}
                    visible={showForm}
                    onCancel={handleFormCancel}
                    footer={null}
                    width={window.innerWidth < 768 ? '95%' : 800}
                    style={{ 
                        top: window.innerWidth < 768 ? 10 : 50,
                        maxHeight: window.innerWidth < 768 ? '90vh' : '80vh'
                    }}
                    bodyStyle={{
                        maxHeight: window.innerWidth < 768 ? '70vh' : '60vh',
                        overflowY: 'auto',
                        padding: window.innerWidth < 768 ? '16px' : '24px'
                    }}
                    destroyOnClose
                >
                    <AdminAnnouncementForm
                        announcement={editingAnnouncement}
                        onSubmit={handleFormSubmit}
                        onCancel={handleFormCancel}
                        loading={formLoading}
                    />
                </Modal>
            </div>
        );
};

export default AdminAnnouncementsScreen;
